import { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './AuthContext';
import OtpVerifyForm from './OtpVerifyForm';
import { IconCheck } from './Icons';
import PasswordInput from './PasswordInput';

const calcStrength = (pw) => {
  const checks = {
    length:    pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number:    /[0-9]/.test(pw),
    special:   /[^A-Za-z0-9]/.test(pw)
  };
  const passed = Object.values(checks).filter(Boolean).length;
  let level = 'weak';
  if (passed >= 5) level = 'strong';
  else if (passed >= 3) level = 'fair';
  return { checks, passed, level };
};

export default function RegisterPage({ onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep]         = useState('form'); // 'form' | 'otp'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [error, setError]       = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focused, setFocused]   = useState(false);
  const [mounted, setMounted]   = useState(false);
  const [closing, setClosing]   = useState(false);
  const hideTimer = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const { register } = useContext(AuthContext);

  const { checks, passed, level } = calcStrength(password);
  const canSubmit = checks.length; // only hard gate is 8 chars
  const shouldShowChecks = focused && password.length > 0;

  useEffect(() => {
    if (shouldShowChecks) {
      clearTimeout(hideTimer.current);
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      hideTimer.current = setTimeout(() => { setMounted(false); setClosing(false); }, 220);
    }
    return () => clearTimeout(hideTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowChecks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { setError(t('auth.passwordMinLength')); passwordRef.current?.focus(); return; }
    setError('');
    setIsLoading(true);
    try {
      await register(email, password, name);
      setStep('otp');
    } catch (err) {
      setError(err.message);
      emailRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'otp') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>{t('auth.verifyYourEmail')}</h1>
          <OtpVerifyForm email={email} onVerified={() => onSuccess()} onBack={() => setStep('form')} />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>{t('auth.createAccount')}</h1>
        <form onSubmit={handleSubmit} className="auth-form">
          <input
            type="text"
            placeholder={t('auth.fullName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            ref={emailRef}
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <PasswordInput
            ref={passwordRef}
            placeholder={t('auth.passwordMin8')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            required
          />

          {mounted && (
            <div className={`strength-wrapper${closing ? ' strength-wrapper-closing' : ''}`}>
              <div className="strength-bar-track">
                <div className={`strength-bar-fill strength-${level}`} style={{ width: `${(passed / 5) * 100}%` }} />
              </div>
              <span className={`strength-label strength-label-${level}`}>
                {level === 'weak' ? t('auth.strengthWeak') : level === 'fair' ? t('auth.strengthFair') : t('auth.strengthStrong')}
              </span>
              <ul className="strength-checks">
                <li className={checks.length    ? 'check-pass' : 'check-fail'}>{checks.length ? <IconCheck size={12} /> : '○'} {t('auth.checkLength')}</li>
                <li className={checks.uppercase ? 'check-pass' : 'check-fail'}>{checks.uppercase ? <IconCheck size={12} /> : '○'} {t('auth.checkUppercase')}</li>
                <li className={checks.lowercase ? 'check-pass' : 'check-fail'}>{checks.lowercase ? <IconCheck size={12} /> : '○'} {t('auth.checkLowercase')}</li>
                <li className={checks.number    ? 'check-pass' : 'check-fail'}>{checks.number ? <IconCheck size={12} /> : '○'} {t('auth.checkNumber')}</li>
                <li className={checks.special   ? 'check-pass' : 'check-fail'}>{checks.special ? <IconCheck size={12} /> : '○'} {t('auth.checkSpecial')}</li>
              </ul>
            </div>
          )}

          {error && <div className="auth-error">{error}</div>}
          <button type="submit" disabled={isLoading || !canSubmit} className="btn submit">
            {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
          </button>
        </form>
      </div>
    </div>
  );
}
